import {
  faUpload,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFileImage,
  faFile,
  faTimes,
  faCamera
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import profilePictureAvatar from 'assets/img/placeholders/profile-picture-avatar.png';
import { convertBase64 } from 'helpers/utils';
import propTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import { Button, Form, Image, Card, Row, Col, Modal } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { ReactComponent as TakePhotoIcon } from 'assets/icons/Take Photo from WhatsApp.svg';
import { ReactComponent as UploadPhotoIcon } from 'assets/icons/Upload Photo from WhatsApp.svg';
import FileUploadPreview from '../custom/FileUploadPreview';
import LogoUploadPreview from '../custom/LogoUploadPreview';

export const RHFFileUpload = ({ name, label, ...rest }) => {
  const [productFile, setProductFile] = useState([]);
  const [previewUrl, setPreviewUrl] = useState('');
  const { control } = useFormContext();
  const { watch } = useFormContext();
  const value = watch(name);

  useEffect(() => {
    if (!value) {
      setProductFile([]);
      setPreviewUrl('');
    } else if (typeof value === 'string' && value?.includes('http')) {
      // If value is a URL, set it as preview
      setPreviewUrl(value);
    } else if (value instanceof File) {
      // If value is a File object, create a preview URL
      const objectUrl = URL.createObjectURL(value);
      setPreviewUrl(objectUrl);

      // Free memory when this component is unmounted
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [value, name]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange }, fieldState: { error } }) => {
        return (
          <Form.Group>
            <Form.Label>{label}</Form.Label>
            <FileUploadPreview
              onChange={e => {
                const file = e.target.files[0];
                onChange(file);
                // Preview is handled by the useEffect
              }}
              files={productFile}
              setFiles={setProductFile}
              previewUrl={previewUrl}
              {...rest}
            />
            {error && (
              <div className="invalid-feedback-custom">{error.message}</div>
            )}
          </Form.Group>
        );
      }}
    />
  );
};

RHFFileUpload.propTypes = {
  name: propTypes.string,
  label: propTypes.string
};

export const RHFLogoUpload = ({ name, ...rest }) => {
  const [productFile, setProductFile] = useState([]);
  const { control } = useFormContext();
  const { watch } = useFormContext();
  const value = watch(name);
  useEffect(() => {
    if (!value) {
      setProductFile([]);
    }
  }, [value, name]);
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        // if value is url then set preview url
        let previewUrl = '';
        if (typeof value === 'string' && value?.includes('http')) {
          previewUrl = value;
        }
        return (
          <Form.Group>
            {/* <Form.Label>{label}</Form.Label> */}
            <LogoUploadPreview
              onChange={e => onChange(e.target.files[0])}
              files={productFile}
              setFiles={setProductFile}
              previewUrl={previewUrl}
              label="Your Logo"
              {...rest}
            />
            {error && (
              <div className="invalid-feedback-custom">{error.message}</div>
            )}
          </Form.Group>
        );
      }}
    />
  );
};

RHFLogoUpload.propTypes = {
  name: propTypes.string
};

export const RHFAvatar = ({ name, ...rest }) => {
  const [base64, setBase64] = useState('');
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        // if value is url then set preview url
        if (typeof value === 'string' && value?.includes('http')) {
          setBase64(value);
        }
        return (
          <>
            <div className="circular-image-center cursor-pointer">
              <div className="circular-image mb-2">
                <label className="circular-image" htmlFor="upload-button">
                  <Image
                    src={base64 ? base64 : profilePictureAvatar}
                    style={{
                      opacity: '.6',
                      borderRadius: '200px',
                      backgroundColor: 'none',
                      height: '100px',
                      width: '100px'
                    }}
                  />
                  <FontAwesomeIcon icon="camera" className="fs-1" />
                </label>
                <input
                  type="file"
                  id="upload-button"
                  style={{ display: 'none' }}
                  accept="image/*"
                  onChange={async e => {
                    const file = e.target.files[0];
                    onChange(file);
                    const base64 = await convertBase64(file);
                    setBase64(base64);
                  }}
                  {...rest}
                />
              </div>
            </div>
            {error && (
              <div className="invalid-feedback-custom">{error.message}</div>
            )}
          </>
        );
      }}
    />
  );
};

RHFAvatar.propTypes = {
  name: propTypes.string,
  label: propTypes.string
};

export const RHFFileUploadRegistration = ({ name, label, ...rest }) => {
  const { control } = useFormContext();
  const [base64, setBase64] = useState('');

  const handleImageClick = () => {
    document.getElementById('upload-button').click();
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        // if value is url then set preview url
        if (typeof value === 'string' && value?.includes('http')) {
          setBase64(value);
        }
        return (
          <Form.Group
            className="px-3 border border-300 rounded d-flex align-items-center"
            {...rest}
          >
            <div
              className="d-flex my-2 rounded-circle"
              style={{
                alignItems: 'center',
                overflow: 'hidden',
                width: 60,
                height: 60,
                cursor: 'pointer'
              }}
              onClick={handleImageClick}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && handleImageClick()}
            >
              <Image
                src={base64 ? base64 : profilePictureAvatar}
                className="w-100 h-100 text-secondary"
              />
            </div>
            <p className="mb-0 fw-medium ms-2">{label}</p>
            <input
              type="file"
              id="upload-button"
              style={{ display: 'none' }}
              accept="image/*"
              onChange={async e => {
                const file = e.target.files[0];
                onChange(file);
                const base64 = await convertBase64(file);
                setBase64(base64);
              }}
              {...rest}
            />
            <Button
              variant="outline-primary"
              className="file-selector my-2 ms-auto"
              onClick={handleImageClick}
            >
              <FontAwesomeIcon icon={faUpload} className="me-2" />
              Upload
            </Button>
            {error && (
              <div className="invalid-feedback-custom">{error.message}</div>
            )}
          </Form.Group>
        );
      }}
    />
  );
};

RHFFileUploadRegistration.propTypes = {
  name: propTypes.string,
  label: propTypes.string
};

// Document Upload Component - supports multiple files with preview and delete
export const RHFDocumentUpload = ({
  name,
  label,
  accept = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg',
  multiple = true,
  maxFiles = 10,
  maxSizeMB = 10,
  ...rest
}) => {
  const { control, watch } = useFormContext();
  const currentFiles = watch(name) || [];

  const getFileIcon = fileName => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return faFilePdf;
      case 'doc':
      case 'docx':
        return faFileWord;
      case 'xls':
      case 'xlsx':
        return faFileExcel;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
        return faFileImage;
      default:
        return faFile;
    }
  };

  const getFileColor = fileName => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return 'text-danger';
      case 'doc':
      case 'docx':
        return 'text-primary';
      case 'xls':
      case 'xlsx':
        return 'text-success';
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
        return 'text-info';
      default:
        return 'text-secondary';
    }
  };

  const formatFileSize = bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileChange = (e, onChange) => {
    const selectedFiles = Array.from(e.target.files || []);

    // Validate file count
    if (currentFiles.length + selectedFiles.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    // Validate file sizes
    const oversizedFiles = selectedFiles.filter(
      file => file.size > maxSizeMB * 1024 * 1024
    );
    if (oversizedFiles.length > 0) {
      alert(`Some files exceed the ${maxSizeMB}MB limit`);
      return;
    }

    // Add new files to existing ones
    const newFiles = multiple
      ? [...currentFiles, ...selectedFiles]
      : selectedFiles;

    onChange(newFiles);
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleRemoveFile = (index, onChange) => {
    const newFiles = currentFiles.filter((_, i) => i !== index);
    onChange(newFiles);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange }, fieldState: { error } }) => (
        <Form.Group {...rest}>
          {label && <Form.Label>{label}</Form.Label>}

          {/* Upload Area */}
          <div
            className="border border-dashed border-300 rounded p-3 text-center bg-light"
            style={{ cursor: 'pointer' }}
            onClick={() =>
              document.getElementById(`doc-upload-${name}`).click()
            }
            onDragOver={e => {
              e.preventDefault();
              e.currentTarget.classList.add('border-primary');
            }}
            onDragLeave={e => {
              e.preventDefault();
              e.currentTarget.classList.remove('border-primary');
            }}
            onDrop={e => {
              e.preventDefault();
              e.currentTarget.classList.remove('border-primary');
              const droppedFiles = Array.from(e.dataTransfer.files);
              handleFileChange({ target: { files: droppedFiles } }, onChange);
            }}
          >
            <FontAwesomeIcon icon={faUpload} className="text-400 fs-2 mb-2" />
            <p className="mb-1 text-700">Click to upload or drag and drop</p>
            <p className="mb-0 text-500 fs--1">
              PDF, DOC, DOCX, XLS, XLSX, PNG, JPG (max {maxSizeMB}MB each)
            </p>
            <input
              type="file"
              id={`doc-upload-${name}`}
              style={{ display: 'none' }}
              accept={accept}
              multiple={multiple}
              onChange={e => handleFileChange(e, onChange)}
            />
          </div>

          {/* File List */}
          {currentFiles.length > 0 && (
            <div className="mt-2">
              {currentFiles.map((file, index) => (
                <div
                  key={index}
                  className="d-flex align-items-center justify-content-between p-2 border rounded mb-1 bg-white"
                >
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon
                      icon={getFileIcon(file.name || file)}
                      className={`${getFileColor(file.name || file)} fs-1 me-2`}
                    />
                    <div>
                      <p
                        className="mb-0 text-truncate"
                        style={{ maxWidth: '200px' }}
                      >
                        {file.name ||
                          (typeof file === 'string'
                            ? file.split('/').pop()
                            : 'File')}
                      </p>
                      {file.size && (
                        <small className="text-500">
                          {formatFileSize(file.size)}
                        </small>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="link"
                    className="text-danger p-0"
                    onClick={e => {
                      e.stopPropagation();
                      handleRemoveFile(index, onChange);
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                </div>
              ))}
              <small className="text-500">
                {currentFiles.length} of {maxFiles} files
              </small>
            </div>
          )}

          {error && (
            <div className="invalid-feedback d-block">{error.message}</div>
          )}
        </Form.Group>
      )}
    />
  );
};

RHFDocumentUpload.propTypes = {
  name: propTypes.string.isRequired,
  label: propTypes.string,
  accept: propTypes.string,
  multiple: propTypes.bool,
  maxFiles: propTypes.number,
  maxSizeMB: propTypes.number
};

// Camera Modal Component for taking photos
const CameraModal = ({ show, onHide, onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [hasStream, setHasStream] = useState(false);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');

  // Acquire the camera when the modal is shown (and re-acquire when the facing
  // mode changes). This effect depends ONLY on `show`/`facingMode` - it must
  // not depend on the stream, otherwise acquiring the stream would re-trigger
  // the effect and restart the camera in an endless loop, which left the
  // preview perpetually re-initialising and made "Take Photo" appear broken.
  useEffect(() => {
    if (!show) return undefined;

    let cancelled = false;

    const start = async () => {
      try {
        setError(null);
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Camera API unavailable');
        }
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode }
        });
        // The modal may have been closed (or switched) while we awaited
        // permission - if so, drop the stream we just acquired.
        if (cancelled) {
          mediaStream.getTracks().forEach(track => track.stop());
          return;
        }
        streamRef.current = mediaStream;
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
        setHasStream(true);
      } catch (err) {
        console.error('Error accessing camera:', err);
        if (!cancelled) {
          setError(
            'Unable to access camera. Please ensure camera permissions are granted and you are on a secure (https) connection.'
          );
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      setHasStream(false);
    };
  }, [show, facingMode]);

  // Re-attach the stream to the <video> element once it has mounted - the
  // modal body may not be in the DOM yet at the moment the stream resolves.
  useEffect(() => {
    if (hasStream && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [hasStream]);

  const handleSwitchCamera = () => {
    setFacingMode(prev => (prev === 'user' ? 'environment' : 'user'));
  };

  const handleCapture = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      canvas.toBlob(
        blob => {
          if (blob) {
            const file = new File([blob], `photo_${Date.now()}.jpg`, {
              type: 'image/jpeg'
            });
            onCapture(file);
            onHide();
          }
        },
        'image/jpeg',
        0.9
      );
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Take Photo</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center p-0">
        {error ? (
          <div className="p-4 text-danger">{error}</div>
        ) : (
          <div
            className="position-relative"
            style={{ backgroundColor: '#000' }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', maxHeight: '60vh' }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-center">
        <Button variant="outline-secondary" onClick={handleSwitchCamera}>
          Switch Camera
        </Button>
        <Button
          variant="primary"
          onClick={handleCapture}
          disabled={!hasStream}
          className="px-4"
        >
          <FontAwesomeIcon icon={faCamera} className="me-2" />
          Capture
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

CameraModal.propTypes = {
  show: propTypes.bool.isRequired,
  onHide: propTypes.func.isRequired,
  onCapture: propTypes.func.isRequired
};

// Inner component for Photo Upload Cards to properly use hooks
const PhotoUploadCardsInner = ({ value, onChange, error, label, ...rest }) => {
  const [previewUrl, setPreviewUrl] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (typeof value === 'string' && value?.includes('http')) {
      setPreviewUrl(value);
    } else if (value instanceof File) {
      const objectUrl = URL.createObjectURL(value);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    } else if (!value) {
      setPreviewUrl('');
    }
  }, [value]);

  const handleFileSelect = async e => {
    const file = e.target.files[0];
    if (file) {
      onChange(file);
    }
  };

  const handleCameraCapture = file => {
    onChange(file);
  };

  return (
    <Form.Group {...rest}>
      {label && <Form.Label>{label}</Form.Label>}

      {/* Preview Section */}
      {previewUrl && (
        <div className="mb-3 text-center">
          <div
            className="d-inline-block position-relative"
            style={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              overflow: 'hidden',
              border: '3px solid #e0e0e0'
            }}
          >
            <Image
              src={previewUrl}
              alt="Preview"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        </div>
      )}

      {/* Cards Section */}
      <Row className="g-3">
        <Col xs={6}>
          <Card
            className="h-100 text-center cursor-pointer border-2 hover-shadow"
            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
            onClick={() => fileInputRef.current?.click()}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#2c7be5';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = '';
              e.currentTarget.style.transform = '';
            }}
          >
            <Card.Body className="d-flex flex-column align-items-center justify-content-center py-4">
              <div
                className="d-flex align-items-center justify-content-center mb-3"
                style={{ width: 60, height: 60 }}
              >
                <UploadPhotoIcon style={{ width: 50, height: 50 }} />
              </div>
              <Card.Title as="h6" className="mb-1">
                Upload Photo
              </Card.Title>
              <Card.Text className="text-muted fs--1">
                Choose from device
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6}>
          <Card
            className="h-100 text-center cursor-pointer border-2 hover-shadow"
            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
            onClick={() => setShowCamera(true)}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#2c7be5';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = '';
              e.currentTarget.style.transform = '';
            }}
          >
            <Card.Body className="d-flex flex-column align-items-center justify-content-center py-4">
              <div
                className="d-flex align-items-center justify-content-center mb-3"
                style={{ width: 60, height: 60 }}
              >
                <TakePhotoIcon style={{ width: 50, height: 50 }} />
              </div>
              <Card.Title as="h6" className="mb-1">
                Take Photo
              </Card.Title>
              <Card.Text className="text-muted fs--1">Use camera</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Camera Modal */}
      <CameraModal
        show={showCamera}
        onHide={() => setShowCamera(false)}
        onCapture={handleCameraCapture}
      />

      {error && (
        <div className="invalid-feedback d-block mt-2">{error.message}</div>
      )}
    </Form.Group>
  );
};

PhotoUploadCardsInner.propTypes = {
  value: propTypes.any,
  onChange: propTypes.func.isRequired,
  error: propTypes.object,
  label: propTypes.string
};

// Photo Upload Cards Component - shows Upload Photo and Take Photo options
export const RHFPhotoUploadCards = ({ name, label, ...rest }) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <PhotoUploadCardsInner
          value={value}
          onChange={onChange}
          error={error}
          label={label}
          {...rest}
        />
      )}
    />
  );
};

RHFPhotoUploadCards.propTypes = {
  name: propTypes.string.isRequired,
  label: propTypes.string
};
